import {Color} from '@material-ui/lab';
import {AgentList} from 'ApplicationEntry/models/AgentList';
import {ApplicantDemographics, matchApplicantDemographicsByType} from 'ApplicationEntry/models/ApplicantDemographics';
import {BillingAgent, matchBillingAgentByType} from 'ApplicationEntry/models/BillingAgent';
import {DependentLists} from 'ApplicationEntry/models/DependentLists';
import {Premium} from 'ApplicationEntry/models/Premium';
import {OrdinaryAccident} from 'ApplicationEntry/models/ProductsRiders/Accident/OrdinaryAccident';
import {Cancer, matchCancerByType} from 'ApplicationEntry/models/ProductsRiders/Cancer/Cancer';
import {Disability} from 'ApplicationEntry/models/ProductsRiders/Disability/Disability';
import {OrdinaryCriticalIllness} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/OrdinaryCriticalIllness';
import {IWL, matchIWLByType} from 'ApplicationEntry/models/ProductsRiders/IWL/IWL';
import * as RI from 'ApplicationEntry/models/ProductsRiders/RiderInformation';
import {OrdShop} from 'ApplicationEntry/models/ProductsRiders/Shop/OrdinaryShop';
import {isNonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {List} from 'immutable';
import * as I from 'immutable';
import DateUtility from 'Shared/utilities/DateUtility';
import {UWQuestions} from 'ApplicationEntry/models/UnderwritingQuestions';
import {CaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import {ContingentBeneficiaryList} from 'ApplicationEntry/models/ContingentBeneficiaryList';
import {PrimaryBeneficiaryList} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import {OwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {PlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {groupDependentProductIdentifier} from './ValidateGroupApplication';
import {GroupCancer, ValidGroupCancer} from 'ApplicationEntry/models/ProductsRiders/Cancer/GroupCancer';
import {GroupDisability} from 'ApplicationEntry/models/ProductsRiders/Disability/GroupDisability';
import {OrdDisability} from 'ApplicationEntry/models/ProductsRiders/Disability/OrdinaryDisability';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';

export type messageType = {
  type: Color;
  value: string;
};

export const validateTICAgent = (agentList: AgentList) => {
  let isTICAgentFound = false;
  agentList.agentList.forEach(item => {
    const agentCode = item.agentNumber.key === 'UserAssignedAgentNumber' ? item.agentNumber.value : '';

    if (agentCode !== '') {
      const firstChar = agentCode.charAt(0);
      if (firstChar.toUpperCase() === 'T') {
        isTICAgentFound = true;
      }
    }
  });
  return isTICAgentFound;
};

const validateAge = (dob: Date | '', issueDate: Date | '') => {
  if (dob !== '' && issueDate !== '') {
    let ageNow = issueDate.getFullYear() - dob.getFullYear();
    const m = issueDate.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && issueDate.getDate() < dob.getDate())) {
      ageNow--;
    }
    const age = isNaN(ageNow) || ageNow < 0 ? 0 : ageNow;
    return age > 50 ? true : false;
  }
  return false;
};

const validateBillingType = (directBill: boolean, pac: boolean) => {
  return directBill || pac ? true : false;
};

const validateAPDSelected = (riderUnits: List<number>, codes: List<string>) => {
  const apdIndex = codes.findIndex(riderCode => riderCode === 'APD');
  return apdIndex !== -1 ? Boolean(riderUnits.get(apdIndex)) : false;
};

//  Critical Illness Validation
export const validateCriticalIllness = (
  criticalIllness: OrdinaryCriticalIllness,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  applicantInfo: ApplicantDemographics,
  agents: AgentList,
  questions: UWQuestions,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  owner: OwnerInfo,
  isQuality: boolean,
  planCodes: PlanCodes
) => {
  const riderCodes = RI.isLoaded(criticalIllness.riderInformation)
    ? criticalIllness.riderInformation.Data.codes
    : I.List<string>();

  const riderAmounts = RI.isLoaded(criticalIllness.riderInformation)
    ? criticalIllness.riderInformation.Data.amounts
    : I.List<string>();

  const messages: messageType[] = [];

  const directBill = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.directBill,
    NewYork: NyApp => NyApp.directBill,
    _: () => false,
  })(billingAgent);

  const stateSigned = getStateSigned(billingAgent);

  riderCodes.forEach((riderItem, riderIndex) => {
    if (riderItem === 'WBR3' || riderItem === 'WBR') {
      if (stateSigned === 'CO' && riderAmounts.get(riderIndex) !== '3.0' && riderAmounts.get(riderIndex) !== '4.0') {
        messages.push({
          type: 'warning',
          value: 'Critical Illness WBR rider can only have 3 or 4 units in CO',
        });
      }
      if (
        getStateSigned(billingAgent) &&
        riderAmounts.get(riderIndex) !== '1.0' &&
        riderAmounts.get(riderIndex) !== '2.0'
      ) {
        messages.push({
          type: 'warning',
          value: 'Critical Illness WBR3 rider can only have 1 or 2 units in MT',
        });
      }
    }
  });

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(criticalIllness.planType)) {
      messages.push({
        type: 'error',
        value: 'Missing Critical Illness plan type',
      });
    }
    if (!isNonEmptyString(criticalIllness.premium)) {
      messages.push({
        type: 'warning',
        value: 'Missing Critical Illness premium',
      });
    }
    if (criticalIllness.benefitAmount === 0) {
      messages.push({
        type: 'error',
        value: 'Missing Critical Illness benefit amount',
      });
    }
    if (!isNonEmptyString(criticalIllness.insuredSC)) {
      messages.push({
        type: 'warning',
        value: 'Missing primary insured smoking code.',
      });
    }
  }

  if (billingAgent.billingMode.value === '') {
    messages.push({
      type: 'error',
      value: 'Missing billing mode',
    });
  }

  if (!isNonEmptyString(criticalIllness.spouseSC) && criticalIllness.planType.value === 'FAMILY') {
    messages.push({
      type: 'error',
      value: 'Missing spouse smoking code.',
    });
  }
  if (billingAgent.issueDate === '' && (Number(criticalIllness.cwa.value) === 0 || criticalIllness.cwa.value === '')) {
    messages.push({
      type: 'warning',
      value: 'Missing issue date with CWA for critical illness policy',
    });
  }
  if (directBill && (Number(criticalIllness.cwa.value) === 0 || criticalIllness.cwa.value === '')) {
    messages.push({type: 'warning', value: 'Missing critical illness CWA with direct billing.'});
  }

  if (criticalIllness.planType.value === 'INDCH' && childDependents(dependentList).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Single-parent critical illness policy selected, but no children entered.',
    });
  }
  if (criticalIllness.planType.value === 'FAMILY' && spouseDependent(dependentList).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Family critical illness policy selected, but no spouse entered.',
    });
  }

  if (criticalIllness.planType.value === 'FAMILY' && childDependents(dependentList).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Family critical illness policy selected, but no children entered.',
    });
  }

  if (criticalIllness.planType.value === 'INDSPS' && spouseDependent(dependentList).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Emp+spouse coverage selected without covered spouse information',
    });
  }

  if (criticalIllness.benefitAmount > 50000) {
    messages.push({
      type: 'warning',
      value: 'Critical Illness benefit amount cannot exceed $50,000.',
    });
  }

  if (isNonEmptyString(criticalIllness.insuredSC)) {
    const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
    if (questionList !== '') {
      const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'Tobacco');
      const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
      const insuredSCAnswer = criticalIllness.insuredSC.value === 'Smoker';
      if (tobaccoAnswer !== insuredSCAnswer) {
        messages.push({
          type: 'warning',
          value: 'Primary Insured Smoker selection and Tobacco Usage question must have the same response ',
        });
      }
    }
  }

  if (isNonEmptyString(criticalIllness.spouseSC)) {
    const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
    if (questionList !== '') {
      const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'SPOUSETOBACCOUSE');
      const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
      const spouseSCAnswer = criticalIllness.spouseSC.value === 'Smoker';
      if (tobaccoAnswer !== spouseSCAnswer) {
        messages.push({
          type: 'warning',
          value: 'Secondary Insured Smoker selection and Tobacco Usage question must have the same response ',
        });
      }
    }
  }

  if (dependentList.dependentList.size > 9) {
    messages.push({type: 'warning', value: 'Too many dependents for Critical Illness plan.'});
  }

  if (stateSigned === 'NJ' && criticalIllness.benefitAmount % 1000 !== 0) {
    messages.push({
      type: 'warning',
      value: 'NJ requires that critical illness base be whole units. (1 Unit = $1000).',
    });
  }

  if (!isQuality && questions.answers.size === 0) {
    messages.push({
      type: 'warning',
      value: 'No question data entered.',
    });
  }

  //Applicant Validation

  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateBeneficiaryInformation(beneficiary);
  if (results != null) messages.push({type: 'warning', value: results});

  if (billingAgent.directBill === false && billingAgent.pac === false) {
    results = validateCaseNumber(caseDetails, billingAgent);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateControlNumber(billingAgent.controlNumber);
    if (results != null) messages.push({type: 'warning', value: results});
  }
  results = validateStateSigned(stateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateIssueDate(billingAgent.issueDate);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  if (messages.length > 0 && isQuality) {
    messages.push({type: 'warning', value: 'Please route application to underwriting'});
  }

  return messages;
};

//  Accident Validation
export const validateAccident = (
  accident: OrdinaryAccident,
  billingAgent: BillingAgent,
  dependentList: DependentLists,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  isQuality: boolean,
  owner: OwnerInfo
) => {
  const cwa = accident.cwa.value;

  const issueDate = matchBillingAgentByType({
    Ordinary: () => billingAgent.issueDate,
    Group: () => billingAgent.issueDate,
    NewYork: () => billingAgent.issueDate,
  })(billingAgent);

  const productType = accident.productType.value;

  const coverageLevel = accident.planType.value;

  const riderAmounts = accident.riderInformation.amounts;

  const riderCodes = accident.riderInformation.codes;

  const billPayForm = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.directBill,
    Group: () => false,
    NewYork: () => false,
  })(billingAgent);

  const stateSigned = getStateSigned(billingAgent);

  const messages: messageType[] = [];

  if (Number(cwa) > 0 && issueDate === '') {
    messages.push({type: 'warning', value: 'Missing issue date with CWA for accident policy'});
  }
  if (coverageLevel === 'FAMILY' && dependentList.dependentList.size < 1) {
    messages.push({type: 'warning', value: 'Missing dependents for accident family plan'});
  }
  if (
    validateAge(applicantInfo.dateOfBirth, issueDate) &&
    validateBillingType(billingAgent.directBill, billingAgent.pac) &&
    validateAPDSelected(riderAmounts, riderCodes)
  ) {
    messages.push({
      type: 'warning',
      value: 'APD rider (with Direct/PAC) should not be issued to applicant over 50 years old.',
    });
  }

  const isTICAgentFound = validateTICAgent(agents);

  if (productType === 'AP2' || productType === 'AP3') {
    if (isTICAgentFound) {
      messages.push({type: 'warning', value: 'TIC Agencies must utilize SAP2 or SAP3 plan codes'});
    }
  }

  if (dependentList.dependentList.count() > 0) {
    dependentList.dependentList.forEach(dependent => {
      if (dependent.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
        });
      }
    });
  }

  if (productType !== '') {
    if (!isNonEmptyString(accident.baseUnits)) {
      messages.push({type: 'warning', value: 'Missing accident base units'});
    }
    if (!isNonEmptyString(accident.planType)) {
      messages.push({type: 'warning', value: 'Missing accident plan code'});
    }
  }

  if ((productType === 'AP2C' || productType === 'AP3C') && stateSigned !== 'FL') {
    messages.push({
      type: 'warning',
      value: 'Plan codes AP2C/AP3C can not be selected with state - ' + stateSigned,
    });
  }

  if (billingAgent.directBill || billingAgent.pac) {
    if ((productType === 'AP2C' || productType === 'AP3C') && stateSigned === 'FL') {
      messages.push({
        type: isQuality ? 'error' : 'warning',
        value:
          'Plan codes AP2C/AP3C can not be selected with state - ' + stateSigned + ' for billing type PAC/Direct Bill',
      });
    }
  }

  if (billPayForm && (Number(cwa) === 0 || cwa === '')) {
    messages.push({type: 'warning', value: 'Missing accident CWA with direct billing'});
  }

  //Applicant Validation

  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    // Defect D-49430
    results = validateBeneficiaryInformation(beneficiary);
    if (results != null) messages.push({type: 'warning', value: results});

    if (messages.length > 0 && isQuality) {
      messages.push({type: 'warning', value: 'Please route application to underwriting'});
    }
  }

  if (billingAgent.directBill === false && billingAgent.pac === false) {
    results = validateCaseNumber(caseDetails, billingAgent);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateControlNumber(billingAgent.controlNumber);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateStateSigned(stateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateIssueDate(billingAgent.issueDate);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  return messages;
};

//  Cancer Validation
export const validateCancer = (
  cancer: Cancer,
  billingAgent: BillingAgent,
  dependentList: DependentLists,
  premium: Premium,
  questions: UWQuestions,
  isQuality: boolean,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  owner: OwnerInfo,
  planCodes: PlanCodes
) => {
  const messages: messageType[] = [];

  let {situsState} = caseDetails as ValidGroupCaseDetails;
  const stateSigned = getStateSigned(billingAgent);

  const riderCodes = matchCancerByType({
    Ordinary: ordinaryApp =>
      RI.isLoaded(ordinaryApp.riderInformation) ? ordinaryApp.riderInformation.Data.codes : I.List<string>(),
    Group: groupApp => groupApp.riderInformation.codes,
    NewYork: newYorkApp => newYorkApp.riderInformation.codes,
  })(cancer);

  const riderAmounts = matchCancerByType({
    Ordinary: ordinaryApp =>
      RI.isLoaded(ordinaryApp.riderInformation) ? ordinaryApp.riderInformation.Data.amounts : I.List<string>(),
    Group: groupApp => groupApp.riderInformation.amounts,
    NewYork: newYorkApp => newYorkApp.riderInformation.amounts,
  })(cancer);

  const reRate = matchCancerByType({
    Ordinary: ordinaryApp => ordinaryApp.reRate,
    Group: () => false,
    NewYork: () => false,
  })(cancer);

  const replacementChecked = matchCancerByType({
    Ordinary: ordinaryApp => ordinaryApp.replacement,
    Group: () => false,
    NewYork: () => false,
  })(cancer);

  const originalAgeValue = matchCancerByType({
    Ordinary: ordinaryApp => ordinaryApp.originalAge,
    Group: () => 0,
    NewYork: () => 0,
  })(cancer);

  const replacementPolicyNumber = matchCancerByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.policyNumber.key === 'ValidPolicyNumber' ? ordinaryApp.policyNumber.value.value : '',
    Group: () => '',
    NewYork: () => '',
  })(cancer);

  const billPayForm = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.directBill,
    Group: () => false,
    NewYork: () => false,
  })(billingAgent);

  let WBR6Selected = false;
  let WBR7Selected = false;

  riderCodes.forEach((riderItem, riderIndex) => {
    if (riderItem !== undefined && riderItem !== '') {
      if (riderAmounts.get(riderIndex) === undefined) {
        messages.push({
          type: 'warning',
          value: 'Rider ' + riderItem + ' should have units or volume associated with it.',
        });
      }

      if (riderItem === 'WBR6') {
        WBR6Selected = true;
      }

      if (riderItem === 'WBR7') {
        WBR7Selected = true;
      }
    }
  });
  const applicationType = matchCaseDetailsByType({
    Group: () => ApplicationType.Group,
    Ordinary: () => ApplicationType.Ordinary,
    NewYork: () => ApplicationType.NewYork,
  })(caseDetails);

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(cancer.planType)) {
      messages.push({type: 'error', value: 'Missing cancer plan type'});
    }
    if (applicationType === ApplicationType.Group) {
      const cancerGroup = cancer as GroupCancer;
      const riderCodesGroup = matchCancerByType({
        Ordinary: ordinaryApp =>
          RI.isLoaded(ordinaryApp.riderInformation) ? ordinaryApp.riderInformation.Data.codes : I.List<string>(),
        Group: groupApp => groupApp.riderInformation.codes,
        NewYork: newYorkApp => newYorkApp.riderInformation.codes,
      })(cancerGroup);
      const riderAmountsGroup = matchCancerByType({
        Ordinary: ordinaryApp =>
          RI.isLoaded(ordinaryApp.riderInformation) ? ordinaryApp.riderInformation.Data.amounts : I.List<string>(),
        Group: groupApp => groupApp.riderInformation.amounts,
        NewYork: newYorkApp => newYorkApp.riderInformation.amounts,
      })(cancerGroup);
      if (!isNonEmptyString(cancerGroup.packageName)) {
        messages.push({
          type: 'error',
          value: 'Missing cancer package',
        });
      }
      if (!isNonEmptyString(cancerGroup.plan)) {
        messages.push({
          type: 'error',
          value: 'Missing cancer plan code',
        });
      }
      if (cancerGroup.plan.value !== 'GVCP3') {
        if (!isNonEmptyString(cancerGroup.planOption)) {
          messages.push({
            type: 'error',
            value: 'Missing cancer plan option',
          });
        }
      }
      if (cancerGroup.replacement && !isValidPolicyNumberPaperElectronic(cancerGroup.policyNumber)) {
        messages.push({
          type: 'warning',
          value: 'Missing replacement policy number',
        });
      }
      if (!isNonEmptyString(cancerGroup.baseUnits)) {
        messages.push({
          type: 'warning',
          value: 'Missing cancer base units',
        });
      }
      if (billPayForm && (Number(cancerGroup.cwa.value) === 0 || cancerGroup.cwa.value === '')) {
        messages.push({
          type: 'warning',
          value: 'Missing cancer CWA with direct bill or PAC',
        });
      }
      if (spouseDependent(dependentList).size === 0 && cancerGroup.planType.value === 'INDSPS') {
        messages.push({
          type: 'warning',
          value: 'Emp+spouse coverage selected without covered spouse information',
        });
      }

      if (childDependents(dependentList).size === 0 && cancerGroup.planType.value === 'INDCH') {
        messages.push({
          type: 'warning',
          value: 'Emp+child coverage selected without covered dependent information',
        });
      }

      if (dependentList.dependentList.size > 0 && cancerGroup.planType.value === 'IND') {
        messages.push({
          type: 'warning',
          value: 'Individual coverage selected with covered dependents.',
        });
      }

      if (childDependents(dependentList).size === 0 && cancerGroup.planType.value === 'FAMILY') {
        messages.push({
          type: 'warning',
          value: 'Family coverage selected without Child covered dependents',
        });
      }

      if (spouseDependent(dependentList).size === 0 && cancerGroup.planType.value === 'FAMILY') {
        messages.push({
          type: 'warning',
          value: 'Family coverage selected without Spouse covered dependents',
        });
      }
      if (cancerGroup.plan.value === 'GVCP2') {
        if (cancerGroup.situsState.value === 'WA' || cancerGroup.situsState.value === 'TN') {
          const iscsriderPresent = riderCodesGroup.some(rider => rider === 'CS');

          if (iscsriderPresent === false) {
            messages.push({
              type: 'warning',
              value: 'Missing state mandated cancer screening benefit',
            });
          }
        }
        if (cancerGroup.situsState.value === 'TN') {
          const isICUriderPresent = riderCodesGroup.some(rider => rider === 'ICU');

          if (isICUriderPresent === false) {
            messages.push({
              type: 'warning',
              value: 'Invalid units for state mandated intensive care benefit',
            });
          }
        }
        if (
          cancerGroup.situsState.value === 'CA' ||
          cancerGroup.situsState.value === 'MT' ||
          cancerGroup.situsState.value === 'PA' ||
          cancerGroup.situsState.value === 'VA'
        ) {
          riderCodesGroup.forEach((riderItem, riderIndex) => {
            if (riderItem === 'CS') {
              if (riderAmountsGroup.get(riderIndex) !== 1) {
                messages.push({
                  type: 'warning',
                  value: 'Invalid units for state mandated cancer screening benefit',
                });
              }
            }
          });
          const isWRriderPresent = riderCodesGroup.some(rider => rider === 'CS');

          if (isWRriderPresent === false) {
            messages.push({
              type: 'warning',
              value: 'Invalid units for state mandated cancer screening benefit',
            });
          }
        }
      }
      if (cancerGroup.plan.value === 'GVCP3') {
        if (cancerGroup.situsState.value === 'WA' || cancerGroup.situsState.value === 'TN') {
          const isWRriderPresent = riderCodesGroup.some(rider => rider === 'WR');

          if (isWRriderPresent === false) {
            messages.push({
              type: 'warning',
              value: 'Missing state mandated cancer screening benefit',
            });
          }
        }
        if (cancerGroup.situsState.value === 'TN') {
          const isICUriderPresent = riderCodesGroup.some(rider => rider === 'ICU');

          if (isICUriderPresent === false) {
            messages.push({
              type: 'warning',
              value: 'Invalid units for state mandated intensive care benefit',
            });
          }
        }
        if (
          cancerGroup.situsState.value === 'CA' ||
          cancerGroup.situsState.value === 'MT' ||
          cancerGroup.situsState.value === 'PA' ||
          cancerGroup.situsState.value === 'VA'
        ) {
          riderCodesGroup.forEach((riderItem, riderIndex) => {
            if (riderItem === 'WR') {
              if (riderAmountsGroup.get(riderIndex) !== 1) {
                messages.push({
                  type: 'warning',
                  value: 'Invalid units for state mandated cancer screening benefit',
                });
              }
            }
          });
          const isWRriderPresent = riderCodesGroup.some(rider => rider === 'WR');

          if (isWRriderPresent === false) {
            messages.push({
              type: 'warning',
              value: 'Invalid units for state mandated cancer screening benefit',
            });
          }
        }
      }
    }
  }
  if (!isQuality) {
    if (applicationType === ApplicationType.Group) {
      if (
        caseDetails.productType.key === 'LoadedPolicyTypes' &&
        caseDetails.productType.selectedPolicyTypes.length === 1
      ) {
        let results = groupDependentProductIdentifier(caseDetails, cancer as GroupCancer, dependentList, isQuality);
        if (results != null) messages.push({type: 'error', value: results});
      }
    }
    if (questions.answers.size === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered.',
      });
    }
    const depCount = dependentList.dependentList.filter(dep => dep.dob === '').size;

    if (depCount > 0) {
      messages.push({
        type: 'warning',
        value: 'Invalid birthdate entered for dependent',
      });
    }
    const applicantDateOfHire = matchApplicantDemographicsByType({
      Group: GroupApp => GroupApp.hireDate,
      _: () => null,
    })(applicantInfo);

    if (applicantDateOfHire === '') {
      messages.push({
        type: 'warning',
        value: "Missing the applicant's date of hire.",
      });
    }
    dependentList.dependentList.forEach(depItem => {
      if (depItem !== null) {
        if (depItem.relationship.value.toLowerCase() === 'spouse' && depItem.sex === applicantInfo.sex)
          messages.push({
            type: 'info',
            value: 'Primary Insured and spouse are same sex',
          });
      }
    });
  }

  if (WBR6Selected && WBR7Selected) {
    messages.push({type: 'error', value: 'Cannot have both fixed and Variable Wellness'});
  }

  if (billPayForm && (Number(cancer.cwa.value) === 0 || cancer.cwa.value === '')) {
    messages.push({type: 'warning', value: 'Missing cancer CWA with direct billing'});
  }

  if (Number(cancer.cwa.value) > 0 && billingAgent.issueDate === '') {
    messages.push({type: 'warning', value: 'Missing issue date with CWA for cancer policy'});
  }

  if ((reRate || replacementChecked) && replacementPolicyNumber === '') {
    messages.push({
      type: 'warning',
      value: 'Missing replacement policy number with replacement/rerate option selected.',
    });
  }

  if (reRate && originalAgeValue === 0) {
    messages.push({type: 'warning', value: 'Missing original age with rerate option on cancer policy.'});
  }

  if (cancer.calculatedPremium.value === '') {
    if ((cancer.premium.value === '' && !isQuality) || (premium.key === 'InvalidPremium' && isQuality)) {
      messages.push({type: 'warning', value: 'Missing cancer premium.'});
    }
  }

  if (cancer.planType.value === 'Family' && dependentList.dependentList.size < 1) {
    messages.push({type: 'warning', value: 'Missing dependents for accident family plan'});
  }

  if (dependentList.dependentList.count() > 0) {
    dependentList.dependentList.forEach(dependent => {
      if (dependent.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
        });
      }
    });
  }

  //Applicant Validation
  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    // Defect D-49430
    results = validateBeneficiaryInformation(beneficiary);
    if (results != null) messages.push({type: 'warning', value: results});
    //  Defect D-49430
    if (messages.length > 0 && isQuality) {
      messages.push({type: 'warning', value: 'Please route application to underwriting'});
    }
  }

  if (billingAgent.directBill === false && billingAgent.pac === false) {
    results = validateCaseNumber(caseDetails, billingAgent);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateControlNumber(billingAgent.controlNumber);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  if (applicationType === ApplicationType.Ordinary) {
    results = validateStateSigned(stateSigned);
    if (results != null) {
      messages.push({type: 'warning', value: results});
    }
  }
  if (applicationType === ApplicationType.Group) {
    if (isQuality && planCodes.replaceProduct) {
      const cancerGroup = cancer as ValidGroupCancer;
      if (cancerGroup.situsState.value === '') {
        messages.push({
          type: 'warning',
          value: 'Missing Situs State',
        });
      }
    }
    if (!isQuality) {
      if (stateSigned === '') {
        messages.push({
          type: 'warning',
          value: 'Missing Situs State',
        });
      }
      const applicantEmailAddress = matchApplicantDemographicsByType({
        Ordinary: ordinaryApp => ordinaryApp.email,
        Group: GrpApp => GrpApp.email,
        _: () => null,
      })(applicantInfo);
      if (applicantEmailAddress?.address === '') {
        messages.push({
          type: 'warning',
          value: "Missing the applicant's Email Address",
        });
      }
    }
  }

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateIssueDate(billingAgent.issueDate);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  return messages;
};

//  IWL Validation
export const validateIWL = (
  iwl: IWL,
  dependents: DependentLists,
  billingAgent: BillingAgent,
  applicantInfo: ApplicantDemographics,
  agents: AgentList,
  questions: UWQuestions,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  owner: OwnerInfo,
  isQuality: boolean,
  planCodes: PlanCodes
) => {
  const messages: messageType[] = [];

  const issueDate = matchBillingAgentByType({
    Ordinary: () => billingAgent.issueDate,
    Group: () => billingAgent.issueDate,
    NewYork: () => billingAgent.issueDate,
  })(billingAgent);

  const cwa = matchIWLByType({
    Ordinary: () => iwl.cwa.value,
    Group: () => iwl.cwa.value,
    NewYork: () => iwl.cwa.value,
  })(iwl);

  const replacementPolicyNumber = matchIWLByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.policyNumber.key === 'ValidPolicyNumber' ? ordinaryApp.policyNumber.value.value : '',
    Group: () => '',
    NewYork: () => '',
  })(iwl);

  const directBill = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.directBill,
    NewYork: NyApp => NyApp.directBill,
    _: () => false,
  })(billingAgent);

  const riderCodes = matchIWLByType({
    Ordinary: ordinaryApp => ordinaryApp.riderInformation.codes,
    Group: groupApp => groupApp.riderInformation.codes,
    NewYork: newYorkApp => newYorkApp.riderCodes,
  })(iwl);

  const applicantType = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.type,
    Group: () => '',
    NewYork: () => '',
  })(applicantInfo);

  const stateSigned = getStateSigned(billingAgent);

  let isAccidentDeathBenefit = false;

  riderCodes.forEach(riderItem => {
    if (riderItem !== '') {
      if (riderItem === 'IWLADB') {
        isAccidentDeathBenefit = true;
      }
    }
  });

  if (billingAgent.billingMode.value === '') {
    messages.push({
      type: 'error',
      value: 'Missing billing mode',
    });
  }

  if (!isNonEmptyString(iwl.spouseInsured) && iwl.plan.value === 'FAMILY') {
    messages.push({
      type: 'error',
      value: 'Missing spouse smoking code.',
    });
  }
  if (billingAgent.issueDate === '' && (Number(iwl.cwa.value) === 0 || iwl.cwa.value === '')) {
    messages.push({
      type: 'warning',
      value: 'Missing issue date with CWA for IWL policy',
    });
  }
  if (directBill && (Number(iwl.cwa.value) === 0 || iwl.cwa.value === '')) {
    messages.push({type: 'warning', value: 'Missing IWL CWA with direct billing.'});
  }

  if (iwl.plan.value === 'INDCH' && childDependents(dependents).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Single-parent IWL policy selected, but no children entered.',
    });
  }
  if (iwl.plan.value === 'FAMILY' && spouseDependent(dependents).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Family IWL policy selected, but no spouse entered.',
    });
  }

  if (iwl.plan.value === 'FAMILY' && childDependents(dependents).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Family IWL policy selected, but no children entered.',
    });
  }

  if (iwl.plan.value === 'INDSPS' && spouseDependent(dependents).size === 0) {
    messages.push({
      type: 'warning',
      value: 'Emp+spouse coverage selected without covered spouse information',
    });
  }

  if (isNonEmptyString(iwl.priInsured)) {
    const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
    if (questionList !== '') {
      const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'Tobacco');
      const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
      const insuredSCAnswer = iwl.priInsured.value === 'Smoker';
      if (tobaccoAnswer !== insuredSCAnswer) {
        messages.push({
          type: 'warning',
          value: 'Primary Insured Smoker selection and Tobacco Usage question must have the same response ',
        });
      }
    }
  }

  if (isNonEmptyString(iwl.spouseInsured)) {
    const questionList = questions.questionList.key === 'Loaded' ? questions.questionList.Data : '';
    if (questionList !== '') {
      const tobaccoQuestionIndex = questionList.findIndex(question => question.description === 'SPOUSETOBACCOUSE');
      const tobaccoAnswer = questions.answers.get(tobaccoQuestionIndex) as boolean;
      const spouseSCAnswer = iwl.spouseInsured.value === 'Smoker';
      if (tobaccoAnswer !== spouseSCAnswer) {
        messages.push({
          type: 'warning',
          value: 'Secondary Insured Smoker selection and Tobacco Usage question must have the same response ',
        });
      }
    }
  }

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (iwl.plan.value === '') {
      messages.push({type: 'error', value: 'Missing IWL plan code'});
    }

    if (iwl.priInsured.value === '') {
      messages.push({type: 'warning', value: 'Missing PI Tobacco Status'});
    }

    if (Number(iwl.faceAmt.value) === 0) {
      messages.push({type: 'warning', value: 'Missing IWL face amount'});
    }

    if (iwl.premium.key === 'Empty' || Number(iwl.premium.value) === 0) {
      messages.push({
        type: 'warning',
        value: 'Missing individual whole life premium',
      });
    }
  }

  if (dependents.dependentList.count() > 0) {
    dependents.dependentList.forEach(dependent => {
      if (dependent.socialSecurityNumber.value === '') {
        messages.push({
          type: 'warning',
          value: `please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
        });
      }
    });
  }

  if (iwl.replacement && replacementPolicyNumber === '') {
    messages.push({
      type: 'warning',
      value: 'Missing replacement policy number with replacement option selected for IWL',
    });
  }

  if (isAccidentDeathBenefit && (iwl.faceAmt.value === '' || Number(iwl.faceAmt.value) === 0)) {
    messages.push({type: 'warning', value: 'Missing IWL accidental death benefit volume'});
  }

  if (dependents.dependentList.size > 9) {
    messages.push({type: 'warning', value: 'Too many dependents for IWL plan.'});
  }

  if (!isQuality && questions.answers.size === 0) {
    messages.push({
      type: 'warning',
      value: 'No question data entered.',
    });
  }

  //Applicant Validation

  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateBeneficiaryInformation(beneficiary);
  if (results != null) messages.push({type: 'warning', value: results});

  if (billingAgent.directBill === false && billingAgent.pac === false) {
    results = validateCaseNumber(caseDetails, billingAgent);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateControlNumber(billingAgent.controlNumber);
    if (results != null) messages.push({type: 'warning', value: results});
  }
  results = validateStateSigned(stateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  if (messages.length > 0 && isQuality) {
    messages.push({type: 'warning', value: 'Please route application to underwriting'});
  }

  const applicantAge = applicantInfo.dateOfBirth !== '' ? DateUtility.getAgeFromDob(applicantInfo.dateOfBirth) : 0;
  if (iwl.key === 'ValidOrdinaryIwl') {
    const riderInfo = iwl.riderInformation;
    const faceAmt = Number(iwl.faceAmt.value);
    riderInfo.codes.forEach((riderCode, index) => {
      const riderAmt = riderInfo.amounts.get(index);
      if (riderCode?.toUpperCase() === 'IWLCT') {
        if (applicantType === 'Child') {
          messages.push({
            type: 'warning',
            value: 'IWLCT rider is not available on child policies.',
          });
        } else {
          if (childDependents(dependents).size === 0) {
            messages.push({
              type: 'warning',
              value: 'Missing child information for IWL child rider',
            });
          } else {
            if (applicantAge > 65) {
              messages.push({
                type: 'warning',
                value: 'IWLCT rider requires the age of the primary insured to be 65 or lesser.',
              });
            }
            if (applicantAge < 18) {
              messages.push({
                type: 'warning',
                value: 'IWLCT rider requires the age of the primary insured to be 18 or older.',
              });
            }
            if (
              childDependents(dependents).some(
                dependent => dependent.dob !== '' && DateUtility.getAgeFromDob(dependent.dob) > 25
              )
            ) {
              messages.push({
                type: 'warning',
                value: 'IWLCT rider requires the age of the child to be 25 or lesser.',
              });
            }
          }
          if (riderAmt === undefined || riderAmt < 2) {
            messages.push({
              type: 'warning',
              value: 'IWLCT rider requires a minimum amount of $2,000',
            });
          }
          if (typeof faceAmt === 'number' && riderAmt !== undefined) {
            const CTRider = riderAmt * 1000;
            if (CTRider > Number(faceAmt)) {
              messages.push({
                type: 'warning',
                value: 'IWLCT rider amount should be less than the face amount',
              });
            }
          }
        }
      } else if (riderCode?.toUpperCase() === 'IWLST') {
        if (applicantType === 'Child' || applicantType === 'Spouse') {
          messages.push({
            type: 'warning',
            value: 'IWLST rider is not available on child/spouse policies.',
          });
        } else {
          const spouse = spouseDependent(dependents)?.get(0);
          if (!spouse) {
            messages.push({
              type: 'warning',
              value: 'Missing spouse information for IWL spouse rider',
            });
          } else {
            const spouseDependentAge = spouse.dob ? DateUtility.getAgeFromDob(spouse.dob) : 0;

            if (spouseDependentAge > 65) {
              messages.push({
                type: 'warning',
                value: 'IWLST rider requires the age of the spouse to be 65 or lesser.',
              });
            }
            if (spouseDependentAge < 18) {
              messages.push({
                type: 'warning',
                value: 'IWLST rider requires the age of the spouse to be 18 or older.',
              });
            }
          }
          if (riderAmt === undefined || riderAmt < 1000) {
            messages.push({
              type: 'warning',
              value: 'IWLST rider requires a minimum amount of $1,000',
            });
          }
          if (typeof faceAmt === 'number' && riderAmt !== undefined) {
            const STRider = faceAmt * 0.5;
            if (riderAmt > STRider) {
              messages.push({
                type: 'warning',
                value: 'IWLST rider amount should be less than and 50% the face amount',
              });
            }
          }
        }
      } else if (riderCode?.toUpperCase() === 'IWLLT') {
        if (applicantType === 'Child') {
          messages.push({
            type: 'warning',
            value: 'IWLLT rider is not available on child policies.',
          });
        } else {
          if (applicantAge < 18) {
            messages.push({
              type: 'warning',
              value: 'IWLLT rider requires the age of the primary insured to be 18 or older.',
            });
          }
          if (applicantAge > 65) {
            messages.push({
              type: 'warning',
              value: 'IWLLT rider requires the age of the primary insured to be 65 or lesser.',
            });
          }
          if (riderAmt === undefined || riderAmt < 10) {
            messages.push({
              type: 'warning',
              value: 'IWLLT rider requires a minimum units of 10%',
            });
          }
          if (typeof faceAmt === 'number' && riderAmt !== undefined) {
            const LTrider = faceAmt * (riderAmt / 100);
            if (LTrider > faceAmt) {
              messages.push({
                type: 'warning',
                value: 'IWLLT rider amount should be less than the face amount',
              });
            }
          }
        }
      } else if (riderCode?.toUpperCase() === 'IWLADB') {
        if (applicantType === 'Child') {
          messages.push({
            type: 'warning',
            value: 'ADB rider is not available on child policies.',
          });
        } else {
          if (applicantAge < 18) {
            messages.push({
              type: 'warning',
              value: 'ADB supplemental benefit requires the age of the primary insured to be 18 or older.',
            });
            if (iwl.priInsured.value === 'Smoker') {
              messages.push({
                type: 'warning',
                value: 'Smoker status requires the age of the primary insured to be 18 or older.',
              });
            }
          }
          if (applicantAge > 55) {
            messages.push({
              type: 'warning',
              value: 'ADB supplemental benefit requires the age of the primary insured to be 55 or lesser.',
            });
          }
        }
      } else if (riderCode?.toUpperCase() === 'IWLCI') {
        if (applicantType === 'Child') {
          messages.push({
            type: 'warning',
            value: 'IWLCI rider is not available on child policies.',
          });
        } else {
          if (applicantAge < 18) {
            messages.push({
              type: 'warning',
              value: 'IWLCI rider requires the age of the primary insured to be 18 or older.',
            });
          }
          if (applicantAge > 70) {
            messages.push({
              type: 'warning',
              value: 'IWLCI rider requires the age of the primary insured to be 70 or lesser.',
            });
          }
          if (!riderInfo.codes.includes('IWLTI')) {
            messages.push({
              type: 'warning',
              value: 'IWLTI rider should select along with IWLCI',
            });
          }
        }
      } else if (riderCode?.toUpperCase() === 'IWLTI') {
        if (
          applicantType === 'Ind' ||
          applicantType === 'Emp' ||
          applicantType === 'Spouse' ||
          applicantType === 'Child'
        ) {
          if (applicantAge > 80) {
            messages.push({
              type: 'warning',
              value: 'IWLTI rider requires the age of the primary insured to be 80 or lesser.',
            });
          }
        } else {
          messages.push({
            type: 'warning',
            value: 'IWLTI rider is available on employee, spouse or child policies.',
          });
        }
      } else if (riderCode?.toUpperCase() === 'IWLWP') {
        if (applicantType === 'Child') {
          messages.push({
            type: 'warning',
            value: 'IWLWP rider is not available on child policies.',
          });
        } else {
          if (applicantAge < 18) {
            messages.push({
              type: 'warning',
              value: 'IWLWP rider requires the age of the primary insured to be 18 or older.',
            });
          }
          if (applicantAge > 55) {
            messages.push({
              type: 'warning',
              value: 'IWLWP rider requires the age of the primary insured to be 55 or lesser.',
            });
          }
        }
      }
    });
  }
  return messages;
};

//  Disability Validation
export const validateDisability = (
  disability: Disability,
  dependentList: DependentLists,
  billingAgent: BillingAgent,
  isQuality: boolean,
  premium: Premium,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  questions: UWQuestions,
  owner: OwnerInfo,
  planCodes: PlanCodes
) => {
  const messages: messageType[] = [];
  let {billingCode} = caseDetails as ValidGroupCaseDetails;
  const answerCount = questions.answers.filter(answer => answer !== '').size;

  const stateSigned = getStateSigned(billingAgent);

  const applicationType = matchCaseDetailsByType({
    Group: () => ApplicationType.Group,
    Ordinary: () => ApplicationType.Ordinary,
    NewYork: () => ApplicationType.NewYork,
  })(caseDetails);

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(disability.plan)) {
      messages.push({type: 'error', value: 'Missing disability plan code'});
    }
    if (applicationType === ApplicationType.Ordinary) {
      const disabilityOrd = disability as OrdDisability;

      if (billingAgent.billingMode.value === '') {
        messages.push({
          type: 'error',
          value: 'Missing billing mode',
        });
      }

      if (!isNonEmptyString(disabilityOrd.occClass)) {
        messages.push({type: 'warning', value: 'Missing disability occupational class code'});
      }
    }

    if (applicationType === ApplicationType.Group) {
      const disabilityGroup = disability as GroupDisability;

      if (isQuality && planCodes.replaceProduct) {
        if (disabilityGroup.situsState.value === '') {
          messages.push({
            type: 'warning',
            value: 'Missing Situs State',
          });
        }
      }
      if (!isQuality) {
        if (stateSigned === '') {
          messages.push({
            type: 'warning',
            value: 'Missing Situs State',
          });
        }
      }

      if (!isNonEmptyString(disabilityGroup.packageSource)) {
        messages.push({
          type: 'error',
          value: 'Missing disability package',
        });
      }

      if (disabilityGroup.replacement && !isValidPolicyNumberPaperElectronic(disabilityGroup.policyNumber)) {
        messages.push({
          type: 'warning',
          value: 'Missing replacement policy number',
        });
      }
      if (disabilityGroup?.gcroprRider === true && disabilityGroup?.section125 === true) {
        messages.push({
          type: 'warning',
          value: 'GCROPR cannot be selected when GVDIP Section 125 requested.',
        });
      }
      if (!isNonEmptyString(disabilityGroup.issueType)) {
        messages.push({
          type: 'warning',
          value: 'Missing Plan Type',
        });
      }

      if (
        !isNonEmptyString(disability.monthlyBenefit) &&
        (isNaN(Number(disability.monthlyBenefit.value)) || Number(disability.monthlyBenefit.value) === 0)
      ) {
        messages.push({type: 'warning', value: 'Missing disability benefit amount'});
      }

      if (Number(disability?.monthlyBenefit.value) < 400) {
        messages.push({
          type: 'warning',
          value: 'Monthly Benefit amount must be at least $400.',
        });
      }
      if (disabilityGroup.issueType.value === 'SI' && Number(disabilityGroup.monthlyBenefit.value) > 6000) {
        messages.push({
          type: 'warning',
          value: 'Monthly Benefit amount exceeds SI limit.',
        });
      }
      if (disabilityGroup.issueType.value === 'GI' && Number(disabilityGroup.monthlyBenefit.value) > 5000) {
        messages.push({
          type: 'warning',
          value: 'Monthly Benefit amount exceeds GI limit.',
        });
      }
      if (
        disabilityGroup?.accident.value !== disabilityGroup?.sickness.value &&
        ((disabilityGroup?.accident.value !== '000' && disabilityGroup?.sickness.value !== '007') ||
          (disabilityGroup?.accident.value === '000' && disabilityGroup?.sickness.value !== '007') ||
          (disabilityGroup?.accident.value !== '000' && disabilityGroup?.sickness.value === '007'))
      ) {
        messages.push({
          type: 'warning',
          value: 'Invalid combination of disability sick days and disability accident days.',
        });
      }

      const maxBenefitAmount = 0.6 * Number(disabilityGroup.monthlySalary.value);

      if (Number(disabilityGroup.monthlyBenefit.value) > maxBenefitAmount) {
        messages.push({
          type: 'warning',
          value: 'Monthly Benefit exceeds allowable criteria.',
        });
      }
      if (!isNonEmptyString(disabilityGroup.sickness)) {
        messages.push({type: 'warning', value: 'Missing number of disability sick days'});
      }
      if (!isNonEmptyString(disabilityGroup.accident)) {
        messages.push({type: 'warning', value: 'Missing number of disability accident days'});
      }
      if (dependentList.dependentList.count() > 0 && disabilityGroup.plan.value === '') {
        messages.push({
          type: 'warning',
          value: 'Dependent has been keyed but were not associated with any produsts. Please Review',
        });
      }
    }

    if (dependentList.dependentList.count() > 0) {
      dependentList.dependentList.forEach(dependent => {
        if (dependent.socialSecurityNumber.value === '') {
          messages.push({
            type: 'warning',
            value: `please enter an SSN for dependent ${dependent.firstName.value}, ${dependent.lastName.value}`,
          });
        }
      });
    }

    if (!isNonEmptyString(disability.type)) {
      messages.push({type: 'warning', value: 'Missing disability plan type'});
    }

    if (!isNonEmptyString(disability.benefitPeriod)) {
      messages.push({type: 'warning', value: 'Missing disability benefit period'});
    }

    if (
      !isNonEmptyString(disability.monthlySalary) &&
      (isNaN(Number(disability.monthlySalary.value)) || Number(disability.monthlySalary.value) === 0)
    ) {
      messages.push({type: 'warning', value: 'Missing monthly salary amount'});
    }
  }

  if (!isQuality) {
    const applicantDateOfHire = matchApplicantDemographicsByType({
      Group: GroupApp => GroupApp.hireDate,
      _: () => null,
    })(applicantInfo);

    if (applicantDateOfHire === '') {
      messages.push({
        type: 'warning',
        value: "Missing the applicant's date of hire.",
      });
    }
    if (answerCount === 0) {
      messages.push({
        type: 'warning',
        value: 'No question data entered',
      });
    }

    const depCount = dependentList.dependentList.filter(dep => dep.dob === '').size;

    if (depCount > 0) {
      messages.push({
        type: 'warning',
        value: 'Invalid birthdate entered for dependent',
      });
    }
  }

  if (
    ((!isNonEmptyString(disability.premium) || isNaN(Number(disability.premium.value))) && !isQuality) ||
    (isQuality && premium.key === 'InvalidPremium')
  ) {
    messages.push({type: 'warning', value: 'Missing disability premium'});
  }

  if (isNaN(Number(disability.monthlySalary.value)) && !isQuality) {
    messages.push({type: 'warning', value: 'Invalid monthly salary'});
  }

  if (isNaN(Number(disability.monthlyBenefit.value)) && !isQuality) {
    messages.push({type: 'warning', value: 'Invalid disability benefit amount'});
  }

  if (billingAgent.directBill && (!isNonEmptyString(disability.cwa) || Number(disability.cwa.value) === 0)) {
    messages.push({type: 'warning', value: 'Missing disability CWA with direct billing'});
  }

  if (Number(disability.cwa.value) > 0 && billingAgent.issueDate === '') {
    messages.push({type: 'warning', value: 'Missing issue date'});
  }

  if (validateBillingType(billingAgent.directBill, billingAgent.pac)) {
    messages.push({type: 'warning', value: 'DI5 with direct bill or PAC'});
    messages.push({type: 'warning', value: '---Please route for underwriting verification'});
  }

  if (billingAgent.pac && !isNonEmptyString(billingAgent.payor)) {
    messages.push({type: 'warning', value: 'Missing payor name with PAC'});
  }

  //Applicant Validation
  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateSpouseGender(dependentList, applicantInfo);
  if (results != null) messages.push({type: 'info', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryEmailAddress(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryPhoneNumber(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateBeneficiaryInformation(beneficiary);
  if (results != null) messages.push({type: 'warning', value: results});

  if (billingAgent.directBill === false && billingAgent.pac === false) {
    results = validateCaseNumber(caseDetails, billingAgent);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateControlNumber(billingAgent.controlNumber);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateStateSigned(stateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateIssueDate(billingAgent.issueDate);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  if (messages.length > 0 && isQuality) {
    messages.push({type: 'warning', value: 'Please route application to underwriting'});
  }
  return messages;
};

//  SHOP Validation
export const validateShop = (
  shop: OrdShop,
  dependents: DependentLists,
  billingAgent: BillingAgent,
  isQuality: boolean,
  premium: Premium,
  agents: AgentList,
  applicantInfo: ApplicantDemographics,
  caseDetails: CaseDetails,
  beneficiary: PrimaryBeneficiaryList,
  contingent: ContingentBeneficiaryList,
  owner: OwnerInfo,
  planCodes: PlanCodes
) => {
  const messages: messageType[] = [];

  const stateSigned = getStateSigned(billingAgent);

  if ((isQuality && planCodes.replaceProduct) || !isQuality) {
    if (!isNonEmptyString(shop.planType)) {
      messages.push({type: 'error', value: 'Missing SHOP plan type'});
    }
    if (shop.baseUnit === 0 || shop.baseUnit === -1) {
      messages.push({type: 'warning', value: 'Missing SHOP base units'});
    }
  }

  if (
    ((!isNonEmptyString(shop.premium) || isNaN(Number(shop.premium.value))) && !isQuality) ||
    (isQuality && premium.key === 'InvalidPremium')
  ) {
    messages.push({type: 'warning', value: 'Missing SHOP premium'});
  }

  if (billingAgent.directBill && (!isNonEmptyString(shop.cwa) || Number(shop.cwa.value) === 0)) {
    messages.push({type: 'warning', value: 'Missing SHOP CWA with direct billing'});
  }

  if (Number(shop.cwa.value) > 0 && billingAgent.issueDate === '') {
    messages.push({type: 'warning', value: 'Missing issue date with CWA for SHOP policy'});
  }

  if (billingAgent.state.value === 'FL' && shop.baseUnit > 5) {
    messages.push({type: 'warning', value: 'Maximum base units for Florida (5) exceeded'});
  }

  if (shop.planType.value === 'FAMILY' && dependents.dependentList.size === 0) {
    messages.push({type: 'warning', value: 'Missing dependents for SHOP family plan'});
  }

  if (shop.planType.value === 'INDCH' && childDependents(dependents).size === 0) {
    messages.push({type: 'warning', value: 'Missing children for SHOP indiv. w/child plan'});
  }

  if (shop.planType.value === 'INDSPS' && spouseDependent(dependents).size === 0) {
    messages.push({type: 'warning', value: 'Missing spouse for SHOP indiv. W/Spouse plan'});
  }

  if (dependents.dependentList.size > 9) {
    messages.push({type: 'warning', value: 'Too many dependents for SHOP plan'});
  }

  shop.riderInformation.codes.forEach((rider, index) => {
    const riderUnit = shop.riderInformation.amounts.get(index);
    if (rider === 'IHR1' && shop.baseUnit > 0 && riderUnit !== undefined && riderUnit > 0) {
      if (Math.abs(shop.baseUnit - 1) < 0.01 && riderUnit > 4) {
        messages.push({type: 'warning', value: 'IHR1 rider cannot have more than 4 units with 1 base unit'});
      } else if (Number(riderUnit) - shop.baseUnit > 2) {
        messages.push({
          type: 'warning',
          value: 'IHR1 rider cannot have more than two units over the base units',
        });
      }

      if (billingAgent.state.value === 'FL' && Number(riderUnit) > 7) {
        messages.push({type: 'warning', value: 'Maximum IHR1 units for Florida (7) exceeded'});
      }
    }
  });

  const signatureState = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    Group: () => '',
    NewYork: () => '',
  })(billingAgent);

  if (
    signatureState === 'UT' &&
    validateBillingType(billingAgent.directBill, billingAgent.pac) &&
    (agents.agentList.size <= 1 || agents.agentList.some(agent => !agent.allstateAffiliated))
  ) {
    messages.push({type: 'warning', value: 'Direct SHOP product not available in state for PPGA'});
  }

  //Applicant Validation
  let results = validatePrimaryInsured(applicantInfo.name);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACTransit(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePACAccount(billingAgent);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySex(applicantInfo.sex);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryDateofBirth(applicantInfo.dateOfBirth);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimarySSN(applicantInfo.socialSecurityNumber.value);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryAddress(applicantInfo.address);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryCity(applicantInfo.city);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryState(applicantInfo.state);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validatePrimaryZip(applicantInfo.zip.value);
  if (results != null) messages.push({type: 'warning', value: results});

  if (!isQuality) {
    results = validatePrimaryHeightFeet(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryWeight(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryHeightInches(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validatePrimaryDateOfHire(applicantInfo);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateBeneficiaryInformation(beneficiary);
  if (results != null) messages.push({type: 'warning', value: results});

  if (billingAgent.directBill === false && billingAgent.pac === false) {
    results = validateCaseNumber(caseDetails, billingAgent);
    if (results != null) messages.push({type: 'warning', value: results});

    results = validateControlNumber(billingAgent.controlNumber);
    if (results != null) messages.push({type: 'warning', value: results});
  }

  results = validateStateSigned(stateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAppSignDate(billingAgent.dateSigned);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateIssueDate(billingAgent.issueDate);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateAgents(agents);
  if (results != null) messages.push({type: 'warning', value: results});

  results = validateFormNumber(billingAgent);
  if (results != null) messages.push({type: 'error', value: results});

  results = validatePrimarySignature(billingAgent.ink);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAgentSignature(agents);
  if (results != null) messages.push({type: 'info', value: results});

  results = validateAppSignature(owner);
  if (results != null) messages.push({type: 'info', value: results});

  if (messages.length > 0 && isQuality) {
    messages.push({type: 'warning', value: 'Please route application to underwriting'});
  }
  return messages;
};

export const childDependents = (dependents: DependentLists) => {
  return dependents.dependentList.filter(
    dependent => dependent.relationship.value.toLowerCase().indexOf('child') !== -1
  );
};

export const spouseDependent = (dependents: DependentLists) => {
  return dependents.dependentList.filter(dependent => dependent.relationship.value.toLowerCase() === 'spouse');
};

export const getStateSigned = (billingAgent: BillingAgent) => {
  const stateSigned = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.signatureState.value,
    Group: grpApp => grpApp.state.value,
    NewYork: () => '',
  })(billingAgent);
  return stateSigned;
};

// General Validation
export const validatePrimaryInsured = (primaryName: StringUnion) => {
  if (!isNonEmptyString(primaryName)) {
    return 'Missing name for primary insured.';
  }
  return null;
};

export const validatePrimarySex = (sex: string) => {
  if (sex === '' || sex === 'UNKNOWN') {
    return 'Missing sex for primary insured.';
  }
  return null;
};

export const validatePrimarySSN = (socialSecurityNumber: string) => {
  if (socialSecurityNumber === '') {
    return 'Missing SSN for primary insured.';
  }
  return null;
};

export const validatePrimaryDateofBirth = (dateOfBirth: '' | Date) => {
  if (dateOfBirth === '') {
    return 'Missing birthdate value for primary insured.';
  }
  return null;
};

export const validatePrimaryAddress = (address: StringUnion) => {
  if (!isNonEmptyString(address)) {
    return 'Missing street address for primary insured.';
  }
  return null;
};

export const validatePrimaryCity = (city: StringUnion) => {
  if (!isNonEmptyString(city)) {
    return 'Missing city for primary insured.';
  }
  return null;
};

export const validatePrimaryState = (state: StringUnion) => {
  if (!isNonEmptyString(state)) {
    return 'Missing state code for primary insured.';
  }
  return null;
};

export const validatePrimaryZip = (zip: string) => {
  if (zip === '') {
    return 'Missing zip code for primary insured.';
  }
  return null;
};

export const validatePrimaryHeightFeet = (applicantInfo: ApplicantDemographics) => {
  const applicantHeightFeet = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.height.feet,
    _: () => null,
  })(applicantInfo);
  if (applicantHeightFeet === '') {
    return "Missing the applicant's height (feet).";
  }
  return null;
};

export const validatePrimaryWeight = (applicantInfo: ApplicantDemographics) => {
  const applicantWeight = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.weight,
    _: () => null,
  })(applicantInfo);
  if (applicantWeight === '') {
    return 'Missing the applicant\'s weight.';
  }
  return null;
};

export const validatePrimaryHeightInches = (applicantInfo: ApplicantDemographics) => {
  const applicantHeightInches = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.height.inches,
    _: () => null,
  })(applicantInfo);
  if (applicantHeightInches === '') {
    return "Missing the applicant's height (inches).";
  }
  return null;
};

export const validatePrimaryDateOfHire = (applicantInfo: ApplicantDemographics) => {
  const applicantDateOfHire = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.hireDate,
    Group: grpApp => grpApp.hireDate,
    _: () => null,
  })(applicantInfo);
  if (applicantDateOfHire === '') {
    return "Missing the applicant's date of hire.";
  }
  return null;
};
export const validatePrimaryEmailAddress = (applicantInfo: ApplicantDemographics) => {
  const applicantEmailAddress = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.email,
    Group: grpApp => grpApp.email,
    _: () => null,
  })(applicantInfo);
  if (applicantEmailAddress?.address === '') {
    return "Missing the applicant's Email Address.";
  }
  return null;
};

export const validatePrimaryPhoneNumber = (applicantInfo: ApplicantDemographics) => {
  const applicantPhoneNumber = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.phone,
    Group: grpApp => grpApp.phone,
    _: () => null,
  })(applicantInfo);
  if (applicantPhoneNumber?.phoneNumber === '') {
    return "Missing the applicant's Phone Number.";
  }
  return null;
};

export const validateBeneficiaryInformation = (beneficiaries: PrimaryBeneficiaryList) => {
  if (beneficiaries.primaryBeneficiaryList.size === 0) {
    return 'Missing beneficiary information';
  }
  return null;
};
export const validateContingentBeneficiaryInformation = (beneficiaries: ContingentBeneficiaryList) => {
  if (beneficiaries.contingentBeneficiaryList.size === 0) {
    return 'Missing contingent beneficiary information';
  }
  return null;
};
export const validateCaseNumber = (caseDetails: CaseDetails, billingAgent: BillingAgent) => {
  const caseNumber = matchCaseDetailsByType({
    Ordinary: () =>
      matchBillingAgentByType({
        Ordinary: ordinaryApp =>
          ordinaryApp.caseNumber.key === 'Loaded'
            ? ordinaryApp.caseNumber.Data
            : ordinaryApp.caseNumber.key === 'UserAssignedCaseNumber'
            ? ordinaryApp.caseNumber.value
            : '',
        _: () => '',
      })(billingAgent),
    Group: groupApp =>
      groupApp.caseNumber.key === 'Loaded'
        ? groupApp.caseNumber.Data
        : groupApp.caseNumber.key === 'UserAssignedCaseNumber'
        ? groupApp.caseNumber.value
        : '',
    NewYork: () =>
      matchBillingAgentByType({
        NewYork: newYorkApp =>
          newYorkApp.caseNumber.key === 'UserAssignedCaseNumber' ? newYorkApp.caseNumber.value : '',
        _: () => '',
      })(billingAgent),
  })(caseDetails);
  if (caseNumber === '') {
    return 'Missing case number';
  }
  return null;
};

export const validateSpouseGender = (dependents: DependentLists, applicantInfo: ApplicantDemographics) => {
  const spouse = dependents.dependentList.find(dependent => dependent.relationship.value.toLowerCase() === 'spouse');
  if (spouse?.sex === applicantInfo.sex) {
    return 'Primary insured and spouse are same sex';
  }
  return null;
};

export const validateControlNumber = (controlNumber: StringUnion) => {
  if (!isNonEmptyString(controlNumber)) {
    return 'Missing the control number.';
  }
  return null;
};
export const validateStateSigned = (stateSigned: string) => {
  if (stateSigned === '') {
    return 'Missing the state signed.';
  }
  return null;
};

export const validateAppSignDate = (signDate: '' | Date) => {
  if (signDate === '') {
    return 'Missing the value for app signed date.';
  }
  return null;
};

export const validateIssueDate = (issueDate: '' | Date) => {
  if (issueDate === '') {
    return 'Missing the value for issue date.';
  }
  return null;
};

export const validateAgents = (agents: AgentList) => {
  if (agents.agentList.size === 0) {
    return 'Missing agent informaton.';
  }
  if (agents.agentList.size > 0) {
    let total = 0;
    agents.agentList.forEach(item => {
      total += item.percent;
    });
    if (total > 0 && total !== 100) {
      return 'Agents total percentage not equal to 100%';
    }
    if (total === 0) {
      return 'Agents total percentage equal to 0';
    }
  }
  return null;
};

export const validateFormNumber = (billingAgent: BillingAgent) => {
  const formNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp =>
      ordinaryApp.formNumber.key === 'UserAssignedFormNumber' ? ordinaryApp.formNumber.value : '',
    NewYork: NYApp => (NYApp.formNumber.key === 'UserAssignedFormNumber' ? NYApp.formNumber.value : ''),
    Group: grpApp => (grpApp.formNumber.key === 'UserAssignedFormNumber' ? grpApp.formNumber.value : ''),
  })(billingAgent);

  if (formNumber === '') {
    return 'Missing the form number.';
  }
  return null;
};

export const validatePrimarySignature = (signature: boolean) => {
  if (signature === false) {
    return 'Missing primary insured signature';
  }
  return null;
};

export const validateAgentSignature = (agents: AgentList) => {
  if (agents.agentList.size > 0) {
    let signed = agents.agentList.filter(item => item.signed);

    if (signed.size === 0) {
      return '"Agent Signed" check box was not selected';
    }
  }
  return null;
};

export const validateAppSignature = (owner: OwnerInfo) => {
  if (!owner.ownerSigned) {
    return '"Owner Signed" check box was not selected';
  }
  return null;
};

export const validatePACTransit = (billingAgent: BillingAgent) => {
  const transitNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.transit.value,
    NewYork: newYorkApp => newYorkApp.transit.value,
    _: () => null,
  })(billingAgent);

  if (billingAgent.pac && transitNumber === '') {
    return 'Missing PAC transit number.';
  }
  return null;
};

export const validatePACAccount = (billingAgent: BillingAgent) => {
  const accountNumber = matchBillingAgentByType({
    Ordinary: ordinaryApp => ordinaryApp.account.value,
    NewYork: newYorkApp => newYorkApp.account.value,
    _: () => null,
  })(billingAgent);

  if (billingAgent.pac && accountNumber === '') {
    return 'Missing PAC account number.';
  }
  return null;
};

export const validateFulltimeStudent = (applicantInfo: ApplicantDemographics) => {
  const applicantType = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.type,
    _: () => null,
  })(applicantInfo);

  const isFullTimeStudent = matchApplicantDemographicsByType({
    Ordinary: ordinaryApp => ordinaryApp.isFullTimeStudent,
    _: () => null,
  })(applicantInfo);
  if (isFullTimeStudent === false && applicantType !== 'Child' && applicantType !== 'Other') {
    return 'Full-Time Student information not entered on a propsed ';
  }
  return null;
};
